import React, { useState, useRef, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { EASE_OUT } from '@/lib/motion';
import ContentBlockRenderer from '../../../../components/courses/editors/ContentBlockRenderer';
import ModulePreview from '../../../../components/courses/ModulePreview';
import DropZone from '../shared/DropZone';
import { uploadFile, getSignedUrl } from '../../../../services/uploads';
import type { CourseModule, ModuleType } from '../../../../types/course';

type EditorMode = 'edit' | 'preview';

export interface ContentEditorOverlayModule {
  id: string;
  title: string;
  type: ModuleType;
  image_url: string | null;
  details: Record<string, unknown>;
}

export interface ContentEditorOverlayProps {
  isOpen: boolean;
  sectionId: string;
  courseId: string;
  module: ContentEditorOverlayModule | null;
  onClose: () => void;
  onSave: (moduleId: string, updates: Record<string, unknown>) => Promise<void>;
}

export default function ContentEditorOverlay({
  isOpen,
  sectionId,
  courseId,
  module,
  onClose,
  onSave,
}: ContentEditorOverlayProps) {
  const [localTitle, setLocalTitle] = useState('');
  const [initialTitle, setInitialTitle] = useState('');
  const [headerImagePreview, setHeaderImagePreview] = useState<string | null>(null);
  const [isLocalBlob, setIsLocalBlob] = useState(false); // true when preview is a blob URL
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState<EditorMode>('edit');
  // Live rich-text HTML bubbled up from the editor so Preview reflects unsaved
  // edits (the editor stays mounted across the toggle, so nothing is lost).
  const [liveHtml, setLiveHtml] = useState<string | null>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  // Registry for child editor cancel callbacks (e.g. RichTextEditor rollback)
  const cancelCallbacksRef = useRef<(() => Promise<void>)[]>([]);

  // Sync local state whenever the module identity or title changes (from API fetch)
  const moduleId = module?.id ?? null;
  const moduleTitle = module?.title ?? '';

  useEffect(() => {
    if (!moduleId) return;
    setLocalTitle(moduleTitle);
    setInitialTitle(moduleTitle);
    setMode('edit');
    setLiveHtml(null);
    cancelCallbacksRef.current = [];
  }, [moduleId, moduleTitle]);

  // Reset image state when switching to a different module
  const prevModuleIdRef = useRef<string | null>(null);
  if (moduleId && moduleId !== prevModuleIdRef.current) {
    prevModuleIdRef.current = moduleId;
    if (isLocalBlob && headerImagePreview) URL.revokeObjectURL(headerImagePreview);
    setHeaderImagePreview(null);
    setIsLocalBlob(false);
  }
  if (!moduleId) {
    prevModuleIdRef.current = null;
  }

  const handleHeaderImageFiles = async (files: File[]) => {
    const file = files[0];
    if (!file || !module) return;

    // Show local preview immediately
    if (isLocalBlob && headerImagePreview) URL.revokeObjectURL(headerImagePreview);
    const blobUrl = URL.createObjectURL(file);
    setHeaderImagePreview(blobUrl);
    setIsLocalBlob(true);

    // Upload to S3 and save the key on the module
    try {
      const uploadResult = await uploadFile({
        file,
        resource_type: 'module_cover',
        resource_id: module.id,
      });
      await onSave(module.id, { image_url: uploadResult.key });
      // Resolve to signed URL to replace the blob preview
      const signed = await getSignedUrl({ key: uploadResult.key });
      URL.revokeObjectURL(blobUrl);
      setHeaderImagePreview(signed.url);
      setIsLocalBlob(false);
    } catch {
      // Keep the blob preview as fallback — it'll be cleared on close
    }
  };

  const removeHeaderImage = async () => {
    if (isLocalBlob && headerImagePreview) URL.revokeObjectURL(headerImagePreview);
    setHeaderImagePreview(null);
    setIsLocalBlob(false);
    // Clear the image_url on the module
    if (module) {
      try { await onSave(module.id, { image_url: null }); } catch { /* error is surfaced by the caller's toast handling */ }
    }
  };

  const handleSave = async () => {
    if (!module || saving || !localTitle.trim()) return;
    setSaving(true);
    try {
      // Flush the latest rich-text HTML alongside the title so a quick Save
      // (before the editor's debounced auto-save fires) never drops content.
      const updates: Record<string, unknown> = { title: localTitle.trim() };
      if (module.type === 'rich_text' && liveHtml !== null) {
        updates.details = { content: liveHtml };
      }
      await onSave(module.id, updates);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  // Resolve stored image_url (S3 key) to a signed URL for display
  const moduleImageUrl = module?.image_url ?? null;
  useEffect(() => {
    if (!moduleImageUrl) return;
    let cancelled = false;
    getSignedUrl({ key: moduleImageUrl }).then((result) => {
      if (!cancelled) {
        setHeaderImagePreview(result.url);
        setIsLocalBlob(false);
      }
    }).catch(() => {
      // Key invalid or expired — ignore
    });
    return () => { cancelled = true; };
  }, [moduleImageUrl]);

  // Reset header image when overlay closes (isOpen goes false)
  const prevIsOpenRef = useRef(isOpen);
  useEffect(() => {
    if (prevIsOpenRef.current && !isOpen) {
      if (isLocalBlob && headerImagePreview) URL.revokeObjectURL(headerImagePreview);
      setHeaderImagePreview(null);
      setIsLocalBlob(false);
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, headerImagePreview, isLocalBlob]);

  const handleCancel = async () => {
    if (saving) return;
    // Rollback: restore initial title
    if (module && localTitle !== initialTitle) {
      try { await onSave(module.id, { title: initialTitle }); } catch { /* error is surfaced by the caller's toast handling */ }
    }
    // Rollback: call registered child cancel callbacks (e.g. RichTextEditor)
    for (const cb of cancelCallbacksRef.current) {
      try { await cb(); } catch { /* error is surfaced by the caller's toast handling */ }
    }
    onClose();
  };

  const isActive = !!localTitle;

  // Build a CourseModule-shaped object so ModulePreview can dispatch to the
  // view registry when the user flips the Edit↔Preview toggle.
  const previewModule: CourseModule | null = module
    ? {
        id: module.id,
        section_id: sectionId,
        type: module.type,
        title: localTitle || 'Untitled',
        subtitle: null,
        image_url: module.image_url,
        // For rich text, prefer the live (unsaved) HTML so Preview always shows
        // exactly what's in the editor — not the stale persisted copy.
        details:
          module.type === 'rich_text' && liveHtml !== null
            ? { ...module.details, content: liveHtml }
            : module.details,
        order_index: 0,
        created_by: null,
        created_at: '',
        updated_at: '',
      }
    : null;

  return (
    <AnimatePresence>
      {isOpen && module && (
        <>
          {/* Backdrop */}
          <motion.div
            key="editor-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: EASE_OUT }}
            className="fixed inset-0 z-40 bg-black/50"
            onClick={handleCancel}
          />

          {/* Card overlay — centered, scrollable within itself */}
          <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none p-4 md:p-8">
            <motion.div
              key="editor-card"
              initial={{ opacity: 0, scale: 0.96, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 16 }}
              transition={{ duration: 0.25, ease: EASE_OUT }}
              className="pointer-events-auto bg-surface-elevated overflow-y-auto w-full max-w-[960px] max-h-[90vh] rounded-2xl"
              style={{
                boxShadow: '4px 7px 28px 0px rgba(0,0,0,0.1)',
                willChange: 'transform, opacity',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex flex-col gap-7 px-9 py-10">

                {/* Edit ↔ Preview toggle */}
                <div className="flex justify-center">
                  <div
                    role="tablist"
                    aria-label="Editor mode"
                    className="inline-flex items-center p-1 rounded-full bg-text-tertiary/10 border border-border-primary/20"
                  >
                    {(['edit', 'preview'] as const).map((opt) => {
                      const active = mode === opt;
                      return (
                        <button
                          key={opt}
                          type="button"
                          role="tab"
                          aria-selected={active}
                          onClick={() => setMode(opt)}
                          className={`px-4 py-1.5 rounded-full text-xs font-bold active:scale-[0.96] transition-[background-color,color,transform] duration-150 ${ active ? 'bg-primary-500 shadow-sm' : 'text-text-secondary hover:text-text-primary' }`}
                        >
                          {opt === 'edit' ? 'Edit' : 'Preview'}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Content Name — floating label pill input (edit mode only;
                    preview mode renders the title via ModulePreview header) */}
                {mode === 'edit' && (
                  <div className="relative">
                    <label
                      className={`
                        absolute left-3.5 px-0.5 z-[1] pointer-events-none
                        bg-surface-elevated
                        transition-[top,font-size,font-weight,color] duration-150
                        ${isActive
                          ? '-top-2 text-xs font-semibold text-text-primary'
                          : 'top-4 text-[15px] font-normal text-text-disabled'
                        }
                      `}
                    >
                      Content Name
                    </label>
                    <input
                      ref={titleInputRef}
                      type="text"
                      value={localTitle}
                      onChange={(e) => setLocalTitle(e.target.value)}
                      placeholder=" "
                      className={`
                        w-full h-14 px-3.5 rounded-full text-[15px] font-normal
                        border outline-none
                        transition-[border-color] duration-150
                        text-text-primary bg-transparent
                        ${isActive
                          ? 'border-grey-500/50'
                          : 'border-grey-500/30'
                        }
                        focus:border-primary-500
                      `}
                      autoFocus
                    />
                  </div>
                )}

                {/* Upload header picture — edit mode only; in preview the header
                    image is shown by ModulePreview itself. */}
                {mode === 'edit' && (
                <div className="flex flex-col items-center gap-4">
                  <h3 className="text-xl font-semibold text-text-primary text-center">
                    Upload the Header Picture for This Content
                  </h3>

                  {headerImagePreview ? (
                    <div className="relative w-full rounded-2xl overflow-hidden group">
                      <img
                        src={headerImagePreview}
                        alt="Content header"
                        className="w-full h-[180px] object-cover"
                      />
                      <button
                        onClick={removeHeaderImage}
                        className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 active:scale-[0.92] transition-[opacity,transform] duration-150"
                        aria-label="Remove image"
                      >
                        <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                          <path d="M1 1l10 10M11 1L1 11" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <DropZone
                      onFilesSelected={handleHeaderImageFiles}
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      maxSizeMB={5}
                      label="Drop or select header image"
                      className="w-full"
                      layout="compact"
                    />
                  )}
                </div>
                )}

                {/* Body — the editor stays mounted across the Edit/Preview toggle
                    (just hidden in preview) so in-progress content is never lost;
                    Preview renders ModulePreview from the live content. */}
                <div className={mode === 'preview' ? 'hidden' : ''}>
                  <ContentBlockRenderer
                    module={module}
                    sectionId={sectionId}
                    courseId={courseId}
                    onUpdate={onSave}
                    onDelete={async () => {}}
                    registerCancelCallback={(cb) => { cancelCallbacksRef.current.push(cb); }}
                    onContentChange={setLiveHtml}
                    embedded
                  />
                </div>
                {mode === 'preview' && previewModule && (
                  <div className="rounded-2xl border border-border-primary/20 px-6 py-6 bg-text-tertiary/5">
                    <ModulePreview module={previewModule} />
                  </div>
                )}

                {/* Footer — Cancel / Save */}
                <div className="flex items-center justify-center gap-4 pt-2">
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={saving}
                    className="inline-flex items-center gap-1.5 px-5 py-1.5 rounded-full border border-secondary text-secondary text-sm font-bold hover:bg-secondary/5 active:scale-[0.97] transition-[background-color,transform] duration-150 disabled:opacity-50 disabled:active:scale-100"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                    </svg>
                    Cancel
                  </button>

                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving || !localTitle.trim()}
                    className="inline-flex items-center gap-1.5 px-5 py-1.5 rounded-full text-white text-sm font-bold min-w-[100px] justify-center hover:brightness-105 active:scale-[0.97] transition-[filter,transform] duration-150 disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
                    style={{
                      background: saving || !localTitle.trim()
                        ? 'rgb(var(--color-primary) / 0.4)'
                        : `linear-gradient(to top, ${'rgb(var(--color-primary-700))'}, rgb(var(--color-primary)) 70%)`,
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
